import axios from "axios";
import { baseUrl } from "./baseUrl";
import { uploadImage, updateImage, batchUploadImages } from './imageApiV2';

const token = (localStorage.getItem('cc-t') || sessionStorage.getItem('cc-t'));

// ==================== PRODUCT/SERVICE CATEGORY API FUNCTIONS ====================

// Create New Product/Service Category
export const createProductCategory = async (data, toast, navigate) => {
    try {
        toast.loading('Creating Product/Service Category...');

        // Upload image to Image API V2 if file provided
        if (data.file && data.file instanceof File) {
            console.log('📤 Uploading product category image to Image API V2...');
            const imageResult = await uploadImage(data.file, 'products', false);

            // Send imageUrl to backend
            data.imageUrl = imageResult.url;
            delete data.file; // Remove file from payload

            console.log('✅ Product category image uploaded:', imageResult);
        } else if (data.file && typeof data.file === 'string') {
            // If file is a string (existing URL), convert to imageUrl
            data.imageUrl = data.file;
            delete data.file;
        }

        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/add-product-cat`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            data: data
        };
        const res = await axios.request(config);
        // console.log('Create response:', res.data); // Debug log
        if (res.data.success) {
            toast.dismiss();
            toast.success(res.data.message);
            setTimeout(() => {
                navigate('/products/product-categories/view-product-categories');
            }, 2000);
        } else if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        } else {
            toast.dismiss();
            toast.error('An unknown error occurred.');
        }
    } catch (err) {
        console.error('Create error:', err);
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// List Product/Service Category
export const productCategoryList = async (setData, toast) => {
    try {
        const config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/view-product-cat`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        if (res.data.success) {
            setData(res.data.categories);
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error("Could not find categories!");
        }
    } catch (err) {
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Update Product/Service Category
export const updateProductCategory = async (id, data, toast, navigate) => {
    try {
        toast.loading('Updating Product/Service Category Details...');

        // Upload image to Image API V2 if new file provided
        if (data.file && data.file instanceof File) {
            console.log('📤 Uploading new product category image to Image API V2...');
            const imageResult = await uploadImage(data.file, 'products', false);

            // Send imageUrl to backend
            data.imageUrl = imageResult.url;
            delete data.file; // Remove file from payload

            console.log('✅ Product category image uploaded:', imageResult);
        } else if (data.file && typeof data.file === 'string') {
            // If file is a string (existing URL), convert to imageUrl
            data.imageUrl = data.file;
            delete data.file;
        }

        const config = {
            method: 'put',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/edit-product-cat`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            data: data
        };
        const res = await axios.request(config);
        // console.log('Update response:', res.data); // Debug log
        if (res.data.success) {
            toast.dismiss();
            toast.success(res.data.message);
            setTimeout(() => {
                navigate('/products/product-categories/view-product-categories');
            }, 2000);
        } else if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        } else {
            toast.dismiss();
            toast.error('An unknown error occurred.');
        }
    } catch (err) {
        console.error('Update error:', err);
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Delete Product/Service Category
export const deleteProductCategory = async (id, setData, toast) => {
    try {
        toast.loading('Deleting Product/Service Category...');
        const config = {
            method: 'delete',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/delete-product-cat?id=${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        if (res.data.success) {
            toast.dismiss();
            setData(res.data.categories);
            toast.success(res.data.message);
            window.location.reload();
        }
        if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        }
    } catch (err) {
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Delete Product/Service Category (for React Query)
export const deleteProductCategoryById = async (id) => {
    try {
        const config = {
            method: 'delete',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/delete-product-cat?id=${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        if (res.data.success) {
            return res.data;
        } else {
            throw new Error(res.data?.error?.message || 'Failed to delete category');
        }
    } catch (err) {
        throw new Error(err.message || 'Something went wrong');
    }
}

// Get Product/Service Category To Edit
export const productCategoryEditView = async (id, data, setData, setActive, setImage, toast) => {
    try {
        const config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/view-product-cat?id=${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        // console.log('Edit view response:', res.data); // Debug log
        
        // Handle different possible response structures
        let category = null;
        
        if (res.data.success) {
            category = res.data.data || res.data.category;
        } else if (res.data._id) {
            // Direct data response
            category = res.data;
        }
        
        if (category) {
            const updatedFields = data.map(field => ({
                ...field, value: category[field.name] || ''
            }));
            setData(updatedFields);
            setActive(category.active);
            setImage(category.imageUrl);
        } else if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        } else {
            toast.error('Failed to load category data');
        }
    } catch (err) {
        console.error('Error in productCategoryEditView:', err);
        toast.dismiss();
        toast.error('Something Went Wrong!');
    }
}

// Get Product/Service Category List
export const getProductCategoryList = async () => {
    try {
        const config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/list-product-cat`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        if (res.data.success) {
            return res.data.categories;
        }
        if (res.data?.error) {
            return [];
        }
    } catch (error) {
        console.log(error);
        return [];
    }
}

// ==================== PRODUCT/SERVICE API FUNCTIONS ====================

// Create New Product/Service
export const createProduct = async (data, logo, additionalImages, toast, navigate) => {
    try {
        toast.loading('Creating Product/Service...');

        // Upload logo to Image API V2 if provided
        if (logo && logo instanceof File) {
            console.log('📤 Uploading product logo to Image API V2...');
            const imageResult = await uploadImage(logo, 'products', false);

            data.imageUrl = imageResult.url;
            data.imageId = imageResult.imageId;
            data.imageVersion = imageResult.version;

            console.log('✅ Product logo uploaded:', imageResult);
        }

        // Upload additional images if provided
        if (additionalImages && additionalImages.length > 0) {
            const filesToUpload = additionalImages.filter(img => img instanceof File);

            if (filesToUpload.length > 0) {
                console.log('📤 Uploading additional images to Image API V2...');
                const uploadedImages = await batchUploadImages(filesToUpload, 'products');

                // Map to the format expected by backend
                data.additionalImages = uploadedImages.map(img => ({
                    url: img.url,
                    imageId: img.imageId,
                    version: img.version
                }));

                console.log('✅ Additional images uploaded:', uploadedImages.length);
            }
        }

        const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/add-product`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            data: data
        };
        const res = await axios.request(config);
        console.log('Create product response:', res.data);
        if (res.data.success) {
            toast.dismiss();
            toast.success(res.data.message);
            setTimeout(() => {
                navigate('/products/view-products');
            }, 2000);
        } else if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        } else {
            toast.dismiss();
            // toast.error('An unknown error occurred.');
        }
    } catch (err) {
        console.error('Create product error:', err);
        toast.dismiss();
        const errorMessage = err.response?.data?.error?.message ||
                             err.response?.data?.message ||
                             'Something Went Wrong!';
        toast.error(errorMessage);
    }
}

// Get Product/Service by ID
export const getProductById = async (id) => {
    try {
        const config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/view-product?id=${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        console.log('Get product response:', res.data);

        if (res.data.success) {
            return res.data.data || res.data.product;
        } else if (res.data._id) {
            return res.data;
        } else {
            throw new Error(res.data?.error?.message || 'Failed to fetch product');
        }
    } catch (err) {
        console.error('Error fetching product:', err);
        throw new Error(err.message || 'Something went wrong');
    }
}

// Update Product/Service
export const updateProduct = async (id, data, logo, additionalImages, toast, navigate) => {
    try {
        toast.loading('Updating Product/Service...');

        // Upload logo to Image API V2 if provided
        if (logo && logo instanceof File) {
            console.log('📤 Uploading product logo to Image API V2...');

            // Use updateImage if imageId exists, otherwise upload new
            let imageResult;
            if (data.imageId) {
                imageResult = await updateImage(data.imageId, logo);
                console.log('✅ Product logo updated to version:', imageResult.version);
            } else {
                imageResult = await uploadImage(logo, 'products', false);
                console.log('✅ Product logo uploaded:', imageResult);
            }

            data.imageUrl = imageResult.url;
            data.imageId = imageResult.imageId;
            data.imageVersion = imageResult.version;
        }

        // Handle additional images
        if (additionalImages && additionalImages.length > 0) {
            const filesToUpload = additionalImages.filter(img => img instanceof File);
            const existingImages = additionalImages.filter(img => typeof img === 'string' || (img && img.url && !img instanceof File));

            // Upload new images
            if (filesToUpload.length > 0) {
                console.log('📤 Uploading additional images to Image API V2...');
                const uploadedImages = await batchUploadImages(filesToUpload, 'products');

                // Combine existing images with new uploads
                data.additionalImages = [
                    ...existingImages.map(img => typeof img === 'string' ? { url: img } : img),
                    ...uploadedImages.map(img => ({
                        url: img.url,
                        imageId: img.imageId,
                        version: img.version
                    }))
                ];

                console.log('✅ Additional images uploaded:', uploadedImages.length);
            } else {
                // Keep existing images
                data.additionalImages = existingImages.map(img => typeof img === 'string' ? { url: img } : img);
            }
        }

        const config = {
            method: 'put',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/edit-product`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            data: { ...data, id }
        };
        const res = await axios.request(config);
        // console.log('Update product response:', res.data);
        if (res.data.success) {
            toast.dismiss();
            toast.success(res.data.message);
            setTimeout(() => {
                navigate('/products/view-products');
            }, 2000);
        } else if (res.data?.error) {
            toast.dismiss();
            toast.error(res.data?.error?.message);
        } else {
            toast.dismiss();
            // toast.error('An unknown error occurred.');
        }
    } catch (err) {
        console.error('Update product error:', err);
        toast.dismiss();
        const errorMessage = err.response?.data?.error?.message ||
                             err.response?.data?.message ||
                             'Something Went Wrong!';
        toast.error(errorMessage);
    }
}

// Delete Product/Service
export const deleteProduct = async (id, toast) => {
    try {
        toast.loading('Deleting Product/Service...');
        const config = {
            method: 'delete',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/delete-product?id=${id}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        // console.log('Delete product response:', res.data);
        if (res.data.success) {
            toast.dismiss();
            toast.success(res.data.message);
            return res.data;
        } else {
            toast.dismiss();
            toast.error(res.data?.error?.message || 'Failed to delete product');
            throw new Error(res.data?.error?.message || 'Failed to delete product');
        }
    } catch (err) {
        console.error('Delete product error:', err);
        toast.dismiss();
        toast.error('Something Went Wrong!');
        throw new Error(err.message || 'Something went wrong');
    }
}

// Get All Products List (updated for list route)
export const getProductsList = async () => {
    try {
        const config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: `${baseUrl}/admin/user/list-product`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
        };
        const res = await axios.request(config);
        // console.log('Products list response:', res.data);
        if (res.data.success) {
            return res.data.data || res.data.products || [];
        }
        return [];
    } catch (error) {
        console.error('Error fetching products:', error);
        return [];
    }
}
